<script lang="ts">
import type { ButtonProps } from '#layers/ui/types';
import type { ModalProps } from '#ui/types';

export interface ConfirmDialogProps {
	title?: string;
	description?: string;
	body?: string;
	confirm?: Partial<ButtonProps>;
	cancel?: boolean | Partial<ButtonProps>;
	close?: boolean | Partial<ButtonProps>;
	dismissible?: boolean;
	ui?: ModalProps['ui'];
}

export interface ConfirmDialogEmits {
	close: [confirm: boolean];
}

export interface ConfirmDialogSlots {
	default: () => any;
	title: () => any;
	description: () => any;
	body: () => any;
	confirm: () => any;
	cancel: () => any;
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { omit } from '#layers/base/utils/object';

const props = withDefaults(defineProps<ConfirmDialogProps>(), {
	cancel: true,
	close: true,
	dismissible: true,
});
const emit = defineEmits<ConfirmDialogEmits>();
const slots = defineSlots<ConfirmDialogSlots>();

const { t } = useI18n();
const proxySlots = omit(slots, ['confirm', 'cancel']);

const confirmProps = computed(() => ({
	variant: 'solid' as const,
	color: 'neutral' as const,
	label: t('common.confirm'),
	onClick: () => emit('close', true),
	...(typeof props.confirm === 'object' ? props.confirm : {}),
}));

const cancelProps = computed(() => ({
	variant: 'ghost' as const,
	color: 'neutral' as const,
	label: t('common.cancel'),
	onClick: () => emit('close', false),
	...(typeof props.cancel === 'object' ? props.cancel : {}),
}));

const ui = computed<ModalProps['ui']>(() => ({
	footer: 'flex items-center justify-end gap-4',
	...props.ui,
}));
</script>

<template>
	<DModal
		:title
		:description
		:body
		:close
		:dismissible
		:ui
	>
		<template v-for="(_, name) in proxySlots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template #footer>
			<slot name="cancel">
				<DButton v-if="cancel" v-bind="cancelProps" />
			</slot>
			<slot name="confirm">
				<DButton v-bind="confirmProps" />
			</slot>
		</template>
	</DModal>
</template>
